/**
 * Central Highcharts configuration.
 * Initializes Highcharts with required modules.
 */
// Import Highstock - must come first
import Highcharts from 'highcharts/highstock';

// imports - these auto-register with Highcharts
import 'highcharts/highcharts-more';
import 'highcharts/modules/sankey';
import 'highcharts/modules/exporting';
import 'highcharts/modules/offline-exporting';
import 'highcharts/modules/full-screen';
import 'highcharts/modules/stock-tools';
import 'highcharts/modules/annotations-advanced';
import 'highcharts/modules/price-indicator';
import 'highcharts/indicators/indicators-all';
import 'highcharts/indicators/apo';
import 'highcharts/indicators/aroon';
import 'highcharts/indicators/aroon-oscillator';
import 'highcharts/indicators/bollinger-bands';
import 'highcharts/indicators/cmo';
import 'highcharts/indicators/dema';
import 'highcharts/indicators/disparity-index';
import 'highcharts/indicators/dpo';
import 'highcharts/indicators/indicators';
import 'highcharts/indicators/macd';
import 'highcharts/indicators/ppo';
import 'highcharts/indicators/psar';
import 'highcharts/indicators/regressions';
import 'highcharts/indicators/roc';
import 'highcharts/indicators/rsi';
import 'highcharts/indicators/tema';
import 'highcharts/indicators/trendline';
import 'highcharts/indicators/trix';

const PRICE_LABEL_GAP_PX = 2;
const PRICE_LABEL_TEXT_Y_OFFSET_PX = 3;
const priceLabelTextBaseY = new WeakMap<Highcharts.SVGElement, number>();

type SeriesWithPriceLabels = Highcharts.Series & {
  lastPrice?: Highcharts.SVGElement;
  lastPriceLabel?: Highcharts.SVGElement;
  lastVisiblePrice?: Highcharts.SVGElement;
  lastVisiblePriceLabel?: Highcharts.SVGElement;
};

type PriceLabel = {
  height: number;
  label: Highcharts.SVGElement;
  y: number;
};

const offsetPriceLabelText = (label: Highcharts.SVGElement): void => {
  const textElement = (label as Highcharts.SVGElement & { text?: Highcharts.SVGElement }).text;
  if (!textElement) return;

  const rawTextY = textElement.attr('y');
  const textY = typeof rawTextY === 'number' ? rawTextY : Number.parseFloat(String(rawTextY));
  if (!Number.isFinite(textY)) return;

  const previousBaseY = priceLabelTextBaseY.get(textElement);
  const isAlreadyOffset =
    previousBaseY !== undefined &&
    Math.abs(textY - (previousBaseY + PRICE_LABEL_TEXT_Y_OFFSET_PX)) < 0.5;
  const baseY = isAlreadyOffset ? previousBaseY : textY;
  priceLabelTextBaseY.set(textElement, baseY);

  textElement.attr({ y: baseY + PRICE_LABEL_TEXT_Y_OFFSET_PX });
};

const getPriceLabel = (series: SeriesWithPriceLabels): PriceLabel | null => {
  const label = series.lastVisiblePriceLabel ?? series.lastPriceLabel;
  if (!label || !series.visible) return null;

  const rawY = label.attr('y');
  const y = typeof rawY === 'number' ? rawY : Number.parseFloat(String(rawY));
  if (!Number.isFinite(y)) return null;

  const height = Math.max(1, Math.round(label.getBBox().height));
  return { label, y, height };
};

const stackPriceIndicators = (chart: Highcharts.Chart): void => {
  chart.series.forEach(series => {
    const indicatorSeries = series as SeriesWithPriceLabels;
    indicatorSeries.lastPrice?.hide();
    indicatorSeries.lastVisiblePrice?.hide();
  });
  const labels = chart.series
    .map(series => getPriceLabel(series as SeriesWithPriceLabels))
    .filter((item): item is PriceLabel => item !== null)
    .sort((left, right) => left.y - right.y);

  if (labels.length === 0) return;

  const minY = chart.plotTop;
  const maxY = chart.plotTop + chart.plotHeight;
  if (labels.length > 1) {
    let previousBottom = minY;

    for (const item of labels) {
      const adjustedY = Math.max(item.y, previousBottom + PRICE_LABEL_GAP_PX);
      item.y = adjustedY;
      previousBottom = adjustedY + item.height;
    }

    const overflowBottom = Math.max(0, previousBottom - maxY);
    if (overflowBottom > 0) {
      for (const item of labels) {
        item.y -= overflowBottom;
      }
    }

    const overflowTop = Math.max(0, minY - labels[0].y);
    if (overflowTop > 0) {
      for (const item of labels) {
        item.y += overflowTop;
      }
    }
  }

  for (const item of labels) {
    item.label.attr({ y: Math.round(item.y) });
    offsetPriceLabelText(item.label);
  }
};

const removeStackHandler = Highcharts.addEvent(Highcharts.Chart, 'render', function () {
  stackPriceIndicators(this);
});

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    removeStackHandler();
  });
}

export { Highcharts };
